import _ from 'lodash'
import * as React from 'react'

import {omitAndMergeProps} from './mergeProps'

/** Apply unhandled props to your component's element.
 *
 * This helper passes unhandled props to your component's top element. This
 * way users of your component can e.g. easily add data- attributes.
 *
 * It requires that you specify the properties you do handle in your
 * component's propTypes.
 *
 * Use it as the first expanded property of your top element, so your explicit
 * properties don't get overriden by accident.
 *
 * @example
 * // Good:
 * render() {
 *   return <div {...forwardProps(this)} onClick={handle}>...</div>
 * }
 *
 * @example
 * // Wrong:
 * render() {
 *   return <div onClick={handle} {...forwardProps(this)}>...</div>
 * }
 *
 * As a second argument, you can provide default values that will be _merged_ with the props.
 * Default `className` & `style` will be concatenated together, while the rest of the props will override the defaults.
 *
 * @example
 * <div {...forwardProps(this, {className: 'foo', children: 'empty!'})/>
 *
 * For functional components or just more control, you can pass props and propTypes as a first parameter:
 *
 * @example
 * <div {...forwardProps({props, propTypes}, {className: 'foo', children: 'empty!'})/>
 *
 */
export default function forwardProps(
  instance:
    | React.Component
    | {
        props: any
        propTypes?: any
      },
  defaultsToMerge?: any | null
): any {
  let props
  let propTypes
  if (instance instanceof React.Component || !_.isPlainObject(instance)) {
    props = instance.props
    // @ts-ignore
    propTypes = instance.constructor.propTypes
  } else {
    ;({props, propTypes} = instance)
  }
  return omitAndMergeProps(props, ['children', propTypes], defaultsToMerge)
}

export function forwardStylingProps(
  props: any,
  defaultsToMerge?: any | null
): any {
  const allowedProps = _.pickBy(props, (value, name) => {
    return (
      name === 'className' || name === 'style' || name.indexOf('data-') === 0
    )
  })

  return forwardProps({props: allowedProps}, defaultsToMerge)
}
